import { useVerticalFlowFormLogic } from "@/components/ClustersPage/VerticalFlowComponents/hooks/useVerticalFlowFormLogic";
import { PERSISTED_VM_CLUSTER_KEY } from "@/utils/cluster";
import { invalidFieldKeysFromFormikErrors } from "@/utils/formikErrors";
import { virtualMachineVerticalFlowAnalytics } from "../analytics";
import { DynamicSupplyHardware, DynamicSupplyHardwareFiltersResponse } from "@/types";

export const useVirtualMachineFormLogic = (
  formData: unknown,
  isLg: boolean,
  fetchHardwareWithFilters: (options?: {
    abortController?: AbortController;
    search?: string;
    filters?: Partial<DynamicSupplyHardwareFiltersResponse>;
  }) => Promise<{
    hardware: DynamicSupplyHardware[];
    filters: DynamicSupplyHardwareFiltersResponse;
  }>
) => {
  return useVerticalFlowFormLogic<"processor" | "environment" | "customize">(formData, isLg, {
    analytics: virtualMachineVerticalFlowAnalytics,
    persistedStorageKey: PERSISTED_VM_CLUSTER_KEY,
    processorStepTitle: "Select Your Virtual Machine Processor",
    defaultStepState: {
      processor: "current",
      environment: "next",
      customize: "next"
    },
    fetchHardwareWithFilters,
    onRetrievePersistedState: () => {
      return {
        processor: "completed",
        environment: "completed",
        customize: "current"
      };
    },
    isSubmitPossible: (_, values, errors) => {
      const formErrors = errors as Record<string, unknown>;

      if (values.supplier !== "external" && !values.vmImageType) {
        return false;
      }

      const hasSelectedKeys = (values.selectedSshKeyIds?.length ?? 0) > 0;
      const hasValidSshKey =
        hasSelectedKeys ||
        (values.sshKeys ?? []).some(
          (sshKey) => Boolean(sshKey.key?.trim()) && Boolean(sshKey.value?.trim())
        );
      if (!hasValidSshKey) {
        return false;
      }

      const blockedFields = new Set(invalidFieldKeysFromFormikErrors(formErrors));
      return !["sshKeys", "selectedSshKeyIds", "githubIds", "vmImageType", "networkServices", "name"].some(
        (field) => blockedFields.has(field)
      );
    },
    isIntermediateStep: () => false,
    getNextStep: (currentStep) => {
      if (currentStep === "processor") return "environment";
      if (currentStep === "environment") return "customize";
      return currentStep;
    },
    getPreviousStep: (currentStep) => {
      if (currentStep === "environment") return "processor";
      if (currentStep === "customize") return "environment";
      return currentStep;
    }
  });
};
